import React, { useMemo } from 'react';
import { Alert, Card, Col, List, Row, Space, Statistic, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { formatMoney } from '@/utils/format';
import { Wave5OverviewHero } from './EpcEstimateWave5Panels';
import { Wave6OverviewPricingStrip, type Wave6Ctx } from './EpcEstimateWave6Panels';

const { Text, Title } = Typography;

export interface EpcEstimateOverviewHealthProps {
  estimateId: string;
  revisionId: string;
  readiness: any;
  total: any;
  wave6Ctx: Wave6Ctx | null;
}

/**
 * Wave 1 health / attention — compose existing readiness + TEC (+ diagnostics).
 * No new workspace-summary endpoint.
 */
export const EpcEstimateOverviewHealth: React.FC<EpcEstimateOverviewHealthProps> = ({
  estimateId,
  revisionId,
  readiness,
  total,
  wave6Ctx,
}) => {
  const diagQ = useQuery({
    queryKey: ['epc-est-diagnostics', estimateId],
    queryFn: () => epcEstimationService.diagnostics(estimateId),
    enabled: !!estimateId,
  });

  const attention = useMemo(() => {
    const items: Array<{ key: string; severity: 'error' | 'warn' | 'info'; title: string }> = [];
    for (const c of readiness?.checks || []) {
      if (c.pending) continue;
      if (!c.ok) {
        items.push({
          key: `chk-${c.key}`,
          severity: 'error',
          title: c.label || c.key,
        });
      }
    }
    const findings = Array.isArray(diagQ.data)
      ? diagQ.data
      : Array.isArray(diagQ.data?.findings)
        ? diagQ.data.findings
        : [];
    for (const f of findings) {
      if (f.severity === 'error' || f.severity === 'warn') {
        items.push({
          key: `diag-${f.code}-${f.message}`,
          severity: f.severity === 'error' ? 'error' : 'warn',
          title: `${f.code}: ${f.message}`,
        });
      }
    }
    return items.slice(0, 12);
  }, [readiness, diagQ.data]);

  const currency = total?.currency || '';

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Wave5OverviewHero total={total} costModel={readiness?.cost_model} />
      <Wave6OverviewPricingStrip ctx={wave6Ctx} />

      <Row gutter={[12, 12]}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Total Estimated Cost"
              value={Number(total?.total_estimated_cost ?? total?.tec ?? 0)}
              formatter={(v) =>
                formatMoney(v, { display: 'plain', maximumFractionDigits: 0 })
              }
              suffix={currency}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic title="Cost Model" value={readiness?.cost_model || '—'} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic title="Pricing" value={readiness?.commercial_pricing || 'NOT_STARTED'} />
          </Card>
        </Col>
      </Row>

      <Card
        size="small"
        title={
          <Space>
            <Title level={5} style={{ margin: 0 }}>
              Attention
            </Title>
            <Tag color={attention.length ? 'orange' : 'green'}>
              {attention.length ? `${attention.length} items` : 'Clear'}
            </Tag>
          </Space>
        }
      >
        {attention.length === 0 ? (
          <Alert type="success" showIcon message="No blocking readiness or diagnostic attention items." />
        ) : (
          <List
            size="small"
            dataSource={attention}
            renderItem={(item) => (
              <List.Item>
                <Tag color={item.severity === 'error' ? 'red' : 'orange'}>{item.severity}</Tag>
                <Text>{item.title}</Text>
              </List.Item>
            )}
          />
        )}
        {diagQ.isError ? (
          <Alert
            style={{ marginTop: 8 }}
            type="info"
            showIcon
            message="Diagnostics unavailable for this user/session — readiness checks still shown."
          />
        ) : null}
      </Card>

      <Card title="Readiness checks" size="small">
        <Space wrap>
          {(readiness?.checks || []).map((c: any) => (
            <Tag key={c.key} color={c.pending ? 'default' : c.ok ? 'green' : 'red'}>
              {c.label} {c.pending ? '(later wave)' : c.ok ? '✓' : '✕'}
            </Tag>
          ))}
        </Space>
      </Card>
      <Text type="secondary" style={{ fontSize: 12 }}>
        Revision {revisionId.slice(0, 8)}… · composed from readiness + totalCostSummary + diagnostics
      </Text>
    </Space>
  );
};
